import Sheet from '../../shared/components/Sheet';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface Doc {
  id: number;
  title: string;
  description: string | null;
  content_type: string;
  file_size_bytes: number | null;
  uploaded_at: string;
  uploaded_by_name: string | null;
  sort_order: number;
  has_markdown: boolean;
  has_pdf: boolean;
}

const ADMIN_ROLES = ['Owner', 'Admin'];

const fmtBytes = (n: number | null): string => {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const fmtDate = (s: string): string => {
  try { return new Date(s).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return s; }
};

const Training: React.FC = () => {
  const { user, sessionToken } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role || '');

  const [docs, setDocs] = useState<Doc[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [info, setInfo] = useState('');
  const [query, setQuery] = useState('');
  const [metadata, setMetadata] = useState<Doc | null>(null);
  const [metadataBusy, setMetadataBusy] = useState(false);

  // Upload form (admin only)
  const [showAdd, setShowAdd] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const reload = useCallback(async () => {
    try {
      const res = await fetch('/api/training-documents', {
        cache: 'no-store',
        headers: { 'X-Session-Token': sessionToken || '' },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setDocs(data.documents || []);
    } catch (e: any) { setErr(e.message); }
    finally { setLoading(false); }
  }, [sessionToken]);

  useEffect(() => { reload(); }, [reload]);

  const fileUrl = (id: number) =>
    `/api/training-documents/${id}/file?token=${encodeURIComponent(sessionToken || '')}`;

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { setErr('Pick a PDF file first'); return; }
    if (!title.trim()) { setErr('Title is required'); return; }
    if (file.type !== 'application/pdf') { setErr('Only PDF files are supported'); return; }
    if (file.size > 25 * 1024 * 1024) { setErr('File too large (max 25MB)'); return; }

    try {
      setUploading(true);
      setErr('');
      setInfo('');

      const buf = await file.arrayBuffer();
      const bytes = new Uint8Array(buf);
      let bin = '';
      const chunk = 0x8000;
      for (let i = 0; i < bytes.length; i += chunk) {
        bin += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)));
      }
      const base64 = btoa(bin);

      const res = await fetch('/api/training-documents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Token': sessionToken || '' },
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim() || null,
          content_type: 'application/pdf',
          file_base64: base64,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Upload failed (${res.status})`);

      setTitle('');
      setDescription('');
      if (fileRef.current) fileRef.current.value = '';
      setShowAdd(false);
      setInfo('Document uploaded.');
      await reload();
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id: number, title: string) => {
    if (!window.confirm(`Remove "${title}" from the training library?`)) return;
    try {
      setErr('');
      const res = await fetch(`/api/training-documents/${id}`, {
        method: 'DELETE',
        headers: { 'X-Session-Token': sessionToken || '' },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Delete failed');
      setInfo('Document removed.');
      await reload();
    } catch (e: any) { setErr(e.message); }
  };

  const renameDoc = async (doc: Doc) => {
    if (!doc.title.trim()) { setErr('Title is required'); return; }
    setMetadataBusy(true);
    try {
      const res = await fetch(`/api/training-documents/${doc.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-Session-Token': sessionToken || '' },
        body: JSON.stringify({
          title: doc.title.trim(),
          description: doc.description?.trim() || null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Update failed');
      setMetadata(null);
      await reload();
    } catch (e: any) { setErr(e.message); } finally { setMetadataBusy(false); }
  };

  if (loading) return <p>Loading…</p>;

  return (
    <div style={{ maxWidth: 900 }}>
      <h2>Training</h2><input type="search" aria-label="Search training" placeholder="Search training" value={query} onChange={e => setQuery(e.target.value)} />
      {metadata && <Sheet title="Edit training details" onClose={() => { if (window.confirm('Discard these metadata edits?')) setMetadata(null); }} busy={metadataBusy}>
      <label>Title<input value={metadata.title} onChange={e => setMetadata({ ...metadata, title: e.target.value })} /></label><label>Description<textarea value={metadata.description || ''} onChange={e => setMetadata({ ...metadata, description: e.target.value })} /></label>
      {err && <p role="alert">{err}</p>}<button disabled={metadataBusy || !metadata.title.trim()} onClick={() => renameDoc(metadata)}>{metadataBusy ? 'Saving…' : 'Save details'}</button></Sheet>}
      <p style={{ color: '#666' }}>
        Reference documents and training material for the team. Tap a card to open the PDF.
      </p>

      {err && <div className="error-banner">{err}</div>}
      {info && (
        <div style={{ background: '#e3f9e5', borderLeft: '4px solid #4caf50', padding: '0.5rem 1rem', marginBottom: '1rem' }}>
          {info}
        </div>
      )}

      {isAdmin && (
        <div style={{ marginBottom: '1rem' }}>
          <button className="salary-btn" onClick={() => setShowAdd(!showAdd)}>
            {showAdd ? '✕ Cancel' : '+ Upload PDF'}
          </button>
        </div>
      )}

      {isAdmin && showAdd && (
        <div className="salary-card" style={{ marginBottom: '1rem' }}>
          <h3 style={{ marginTop: 0 }}>Upload a training document</h3>
          <div className="form-row">
            <label>Title <span style={{ color: '#d32f2f' }}>*</span></label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Customer Service Cards" />
          </div>
          <div className="form-row">
            <label>Description (optional)</label>
            <textarea
              value={description}
              rows={2}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short note for the team about this document."
            />
          </div>
          <div className="form-row">
            <label>PDF file (max 25MB) <span style={{ color: '#d32f2f' }}>*</span></label>
            <input type="file" accept="application/pdf" ref={fileRef} />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
            <button className="salary-btn" onClick={upload} disabled={uploading}>
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        </div>
      )}

      {docs.length === 0 ? (
        <p style={{ color: '#666' }}>No training documents yet.</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.75rem' }}>
          {docs.map((d) => (
            <div
              key={d.id}
              className="salary-card"
              style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', minHeight: 180 }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.5rem' }}>
                <div style={{ fontSize: '2rem', lineHeight: 1 }}>📄</div>
                {isAdmin && (
                  <div style={{ display: 'flex', gap: '0.25rem' }}>
                    <button
                      className="salary-btn secondary"
                      style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                      onClick={() => renameDoc(d)}
                    >
                      Edit
                    </button>
                    <button
                      className="salary-btn danger"
                      style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                      onClick={() => remove(d.id, d.title)}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
              <div style={{ fontWeight: 600, fontSize: '1rem' }}>{d.title}</div>
              {d.description && (
                <div style={{ color: '#555', fontSize: '0.85em', flex: 1 }}>{d.description}</div>
              )}
              <div style={{ color: '#888', fontSize: '0.75em' }}>
                {fmtBytes(d.file_size_bytes)} · added {fmtDate(d.uploaded_at)}
                {d.uploaded_by_name ? ` by ${d.uploaded_by_name}` : ''}
              </div>
              {d.has_markdown ? (
                <Link
                  to={`/training/${d.id}`}
                  className="salary-btn"
                  style={{ textAlign: 'center', textDecoration: 'none' }}
                >
                  Read
                </Link>
              ) : d.has_pdf ? (
                <a
                  href={fileUrl(d.id)}
                  target="_blank"
                  rel="noreferrer"
                  className="salary-btn"
                  style={{ textAlign: 'center', textDecoration: 'none' }}
                >
                  Open PDF
                </a>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Training;
